import React, { useEffect, useState } from 'react';

import { Box, Button, ButtonProps, styled, Theme } from '@mui/material';

type Props = {
  children: React.JSX.Element | React.JSX.Element[];
  disabled?: boolean;
  onClick?: () => void;
  filled?: boolean;
};

interface IconBoxProps {
  filled?: boolean;
}

const iconBoxStyles = ({ theme, filled }: { theme: Theme; filled?: boolean }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  boxShadow: `0px 1px 4px ${theme.palette.extendedGrey?.light}`,
  backgroundColor: 'transparent',
  width: 30,
  height: 30,
  '&.IconBox-Disabled': {
    backgroundColor: theme.palette.extendedGrey?.light,
    '& path, rect': {
      stroke: '#B3BFD0',
    },
  },
  '&:not(.IconBox-Disabled):hover': {
    backgroundColor: theme.palette.extendedGrey?.lightest,
    '& path': {
      stroke: theme.palette.primary.main,
      ...(filled && { fill: theme.palette.primary.main }),
    },
    '& rect': {
      ...(filled && { fill: theme.palette.primary.main }),
    },
  },
});

const StyledIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'filled',
})<IconBoxProps>(({ theme, filled }) => iconBoxStyles({ theme, filled }));
const StyledIconButton = styled(Button)<ButtonProps>(({ theme }) => iconBoxStyles({ theme }));

export const ActionTableButton: React.FC<Props> = (props) => {
  const { children, onClick, disabled, filled, ...other } = props;
  const handleOnClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };
  return (
    <StyledIconBox
      {...other}
      filled={filled}
      onClick={handleOnClick}
      className={disabled ? 'IconBox-Disabled' : ''}>
      {children}
    </StyledIconBox>
  );
};

export const DownloadIconButton: React.FC<
  Props & {
    href: string;
    download: boolean;
    delayAccessible?: number;
  }
> = (props) => {
  const {
    children,
    onClick,
    disabled: disabledProp = false,
    href,
    download,
    delayAccessible,
    ...other
  } = props;

  const [enabled, setEnabled] = useState(delayAccessible === undefined);

  useEffect(() => {
    let cancelled = false;

    if (delayAccessible !== undefined && !disabledProp) {
      const timeout = setTimeout(() => {
        if (!cancelled) setEnabled(true);
      }, delayAccessible);
      return () => {
        cancelled = true;
        clearTimeout(timeout);
      };
    } else {
      setEnabled(!disabledProp);
    }
  }, [delayAccessible, disabledProp]);

  const handleOnClick = () => {
    if (!disabledProp && enabled && onClick) {
      onClick();
    }
  };

  return (
    <StyledIconButton
      {...other}
      component="a"
      onClick={handleOnClick}
      href={href}
      className={!enabled ? 'IconBox-Disabled' : ''}
      {...({ download } as any)}
      target="_blank"
      disabled={!enabled}
      sx={{
        '& path': {
          stroke: '#7C8FAC',
        },
      }}>
      {children}
    </StyledIconButton>
  );
};
